import { Enum_LiveStream_Status, Enum_LiveStream_User_type } from '@core/api';
import { byElapsedTimeDesc } from '@core/livestream/helpers';
import omit from '@core/lodash/omit';
import uniqBy from '@core/lodash/uniqBy';
import { logInfo, logMessage } from '@core/logging';
import { fixIncomingProducts } from '@core/shopping/utils/product';
import { normalizeTriviaGiveawayModes } from '@embed/components/VideoPlayer/Player3/store/helpers';
import { updateVideoLiveStreamParams } from '@embed/helpers';
import { fetcher } from '@embed/tools/fetchers';
import { disableEntityReducer, enableEntityReducer, giveawayCollectionReducer, highlightEntitiesReducer, highlightEntityReducer, setLiveStreamPinnedMessageReducer, setLiveStreamTemporaryChatHighlightMessageReducer, unhighlightEntitiesReducer, unhighlightEntityReducer, unsetLiveStreamPinnedMessageReducer, unsetLiveStreamTemporaryChatHighlightMessageReducer, } from './reducers';
import { liveStreamInteractionSelector, liveStreamMessagesSelector, } from './selectors';
import { ILiveStreamChatInputState } from './types';
/**
 *
 */
export const actions = (set, get) => ({
    setTapToUnmuteVisible: (action) => set({ isTapToUnmuteVisible: action }, false, 'setTapToUnmuteVisible'),
    /**
     * Set low latency mode for IVS player
     * @param enabled - Whether low latency mode is enabled
     */
    setIvsLowLatencyEnabled: (enabled) => set({ ivsLowLatencyEnabled: enabled }, false, 'setIvsLowLatencyEnabled'),
    /**
     * Set IVS Quality object enabled setting
     * @param quality - The IVS Quality object to set or undefined for `auto`
     */
    setIvsQuality: (quality) => set({ ivsQuality: quality }, false, 'setIvsQuality'),
    /**
     * Sets the video to play in Player3 instance. This video can differ from the initial
     * if the video is an ad or interactive journey..
     */
    setVideo: (action) => set((state) => {
        const relatedLiveStream = Object.values(state.liveStreams).find((liveStream) => liveStream.video_id === action.video.id) ?? null;
        const updatedParams = updateVideoLiveStreamParams(action.video, relatedLiveStream);
        const video = updatedParams
            ? {
                ...action.video,
                ...updatedParams,
            }
            : action.video;
        return {
            video,
        };
    }, false, 'setVideo'),
    updateVideo: (action) => {
        set((state) => ({
            video: { ...state.video, ...action.video },
        }), false, 'updateVideo');
    },
    setVideoAds: (action) => {
        set((state) => {
            const newAds = action.ads.reduce((obj, item) => {
                obj[item.encoded_id] = item;
                return obj;
            }, {});
            if (Object.keys(newAds).length === 0) {
                return state;
            }
            return {
                videoAdsAll: {
                    ...state.videoAdsAll,
                    ...newAds,
                },
                videoAds: {
                    ...state.videoAds,
                    ...newAds,
                },
            };
        }, false, 'setVideoAds');
    },
    setNextVideoAd: (action) => {
        if (action.video.encoded_id !== get().nextVideoAdId) {
            set({ nextVideoAdId: action.video.encoded_id }, false, 'setNextVideoAd');
        }
    },
    setCurrentVideoAd: (action) => {
        set({
            currentVideoAdId: action.video.encoded_id,
        }, false, 'setCurrentVideoAd');
    },
    discardIfVideoAd: (action) => {
        const { videoId } = action;
        if (videoId in get().videoAds) {
            set((state) => ({
                nextVideoAdId: null,
                currentVideoAdId: null,
                videoAds: Object.fromEntries(Object.entries(state.videoAds).filter(([key]) => key !== videoId)),
            }), false, 'discardIfVideoAd');
        }
    },
    clearVideos: () => set({
        nextVideoAdId: null,
    }, false, 'clearVideos'),
    setSubtitlesEnabled: (action) => set({ subtitlesEnabled: action }, false, 'setSubtitlesEnabled'),
    setCurrentSubtitlesId: (action) => set({
        currentSubtitlesId: action,
    }, false, 'setCurrentSubtitlesId'),
    /** Cart */
    revealCart: (productAddedToCart = false) => set({ isCartRevealed: true, productAddedToCart }, false, 'revealCart'),
    hideCart: () => set({ isCartRevealed: false, productAddedToCart: false }, false, 'hideCart'),
    /** Shopping */
    setVideoProducts: ({ videoId, products, }) => {
        set((state) => ({
            videoProducts: {
                ...state.videoProducts,
                [videoId]: products,
            },
        }), false, 'setVideoProducts');
    },
    setEngagedVideo: (videoId) => set({ engagedVideoId: videoId }, false, 'setEngagedVideo'),
    setEngagedVideoV2: (videoId) => set({ engagedVideoIdV2: videoId }, false, 'setEngagedVideoV2'),
    setIsVideoEngaged: (isEngaged) => set({ isVideoEngaged: isEngaged }, false, 'setIsVideoEngaged'),
    setIsVideoEngagedV2: (isEngaged) => set({ isVideoEngagedV2: isEngaged }, false, 'setIsVideoEngagedV2'),
    /* Interactable */
    setInteractable: () => set({ isInteractable: true }, false, 'setInteractable'),
    unsetInteractable: () => set({ isInteractable: false }, false, 'unsetInteractable'),
    revealProductModal: (source) => set({ isProductModalExpanded: source }, false, 'revealProductModal'),
    hideProductModal: () => set({ isProductModalExpanded: null }, false, 'hideProductModal'),
    setQRMode: (action) => set({ isQRModeEnabled: action }, false, 'setQRMode'),
    setExpandedProduct: (product) => set({
        expandedProduct: product,
        isProductDescriptionExpanded: false,
    }, false, 'setExpandedProduct'),
    setExpandedProductDescription: (isExpanded) => {
        set({
            isProductDescriptionExpanded: isExpanded,
        }, false, 'setExpandedProductDescription');
    },
    addActiveInteractionId: (interactionId) => set((state) => {
        const { activeInteractionIds } = state;
        if (activeInteractionIds.includes(interactionId)) {
            return state;
        }
        return {
            activeInteractionIds: [...activeInteractionIds, interactionId],
        };
    }, false, 'addActiveInteractionId'),
    removeActiveInteractionId: (interactionId) => set((state) => {
        const { activeInteractionIds } = state;
        return {
            activeInteractionIds: activeInteractionIds.filter((id) => id !== interactionId),
        };
    }, false, 'removeActiveInteractionId'),
    addVisibleInteractionId: (interactionId) => set((state) => {
        const { visibleInteractionIds } = state;
        if (visibleInteractionIds.includes(interactionId)) {
            return state;
        }
        return {
            visibleInteractionIds: [...visibleInteractionIds, interactionId],
        };
    }, false, 'addVisibleInteractionId'),
    removeVisibleInteractionId: (interactionId) => set((state) => {
        const { visibleInteractionIds } = state;
        return {
            visibleInteractionIds: visibleInteractionIds.filter((id) => id !== interactionId),
        };
    }, false, 'removeVisibleInteractionId'),
    setExpandedInteractionId: (interactionId) => set({ expandedInteractionId: interactionId }, false, 'setExpandedInteractionId'),
    /** LiveStream */
    updateIvsState: (action) => set((state) => ({
        ivsState: { ...state.ivsState, ...action },
    }), false, 'updateIvsState'),
    fetchLiveStreamDetails: async (action) => {
        const { liveStreamId } = action;
        try {
            const apiHost = get().params?.api_host;
            const response = await fetcher({
                url: `/api/live_streams/${liveStreamId}/detail`,
                baseUrl: apiHost,
            });
            if (!response.ok) {
                throw response;
            }
            const liveStream = normalizeTriviaGiveawayModes(await response.json());
            set((state) => {
                const previousLiveStream = state.liveStreams[liveStreamId] || {};
                liveStream.products = fixIncomingProducts(liveStream.products);
                const updatedParams = updateVideoLiveStreamParams(state.video, liveStream, state.params?.force_replay);
                return {
                    video: updatedParams
                        ? {
                            ...state.video,
                            ...updatedParams,
                        }
                        : state.video,
                    liveStreams: {
                        ...state.liveStreams,
                        [liveStreamId]: {
                            ...previousLiveStream,
                            ...liveStream,
                        },
                    },
                    liveStreamProducts: {
                        ...state.liveStreamProducts,
                        [liveStreamId]: liveStream.products,
                    },
                    deals: {
                        ...state.deals,
                        [liveStreamId]: liveStream.deals,
                    },
                };
            }, false, 'fetchLiveStreamDetailsSucceeded');
        }
        catch (error) {
            logMessage(`Fetch live stream details failed with error: ${error}`, undefined, 1);
        }
    },
    setLiveStreamProducts: (liveStreamId, products) => {
        set((state) => {
            return {
                liveStreamProducts: {
                    ...state.liveStreamProducts,
                    [liveStreamId]: products,
                },
            };
        });
    },
    updateLiveStreamDetails: (action) => {
        const { liveStreamId, liveStream } = action;
        const normalizedLiveStream = {
            ...normalizeTriviaGiveawayModes(liveStream),
        };
        set((state) => {
            const previousLiveStream = state.liveStreams[liveStreamId] || {};
            const newLivestream = {
                ...normalizedLiveStream,
                // Only update the hearts_count if it increases
                hearts_count: typeof liveStream.hearts_count !== 'undefined'
                    ? Math.max(previousLiveStream.hearts_count ?? 0, liveStream.hearts_count ?? 0)
                    : previousLiveStream.hearts_count,
            };
            const updatedParams = updateVideoLiveStreamParams(state.video, newLivestream);
            return {
                video: updatedParams
                    ? {
                        ...state.video,
                        ...updatedParams,
                    }
                    : state.video,
                liveStreams: {
                    ...state.liveStreams,
                    [liveStreamId]: {
                        ...previousLiveStream,
                        ...newLivestream,
                    },
                },
            };
        }, false, 'updateLiveStreamDetails');
    },
    applyLiveStreamStateSnapshot: ({ liveStreamId, data, }) => {
        if (!data.state_snapshot) {
            return;
        }
        const liveStream = get().liveStreams[liveStreamId];
        const snapshot = JSON.parse(data.state_snapshot);
        if (snapshot.highlighted_entities?.length) {
            set((state) => highlightEntitiesReducer(state, {
                liveStreamId,
                entities: snapshot.highlighted_entities,
            }), false, 'applyLiveStreamStateSnapshot-highlightEntities');
        }
        else if (liveStream?.highlighted_entities?.length) {
            // unhighlight entities
            const entities = liveStream.highlighted_entities;
            set((state) => unhighlightEntitiesReducer(state, {
                liveStreamId,
                entities,
            }), false, 'applyLiveStreamStateSnapshot-unhighlightEntities');
            set((state) => ({
                activeInteractionIds: state.activeInteractionIds.filter((id) => id !== entities[0].entity_id),
            }));
            set((state) => ({
                visibleInteractionIds: state.visibleInteractionIds.filter((id) => id !== entities[0].entity_id),
            }));
        }
        else if (snapshot?.highlighted_entity?.entity_type) {
            set((state) => highlightEntityReducer(state, {
                liveStreamId,
                entityId: snapshot.highlighted_entity.entity_id,
                entityType: snapshot.highlighted_entity.entity_type,
            }), false, 'applyLiveStreamStateSnapshot-highlightEntity');
        }
        else {
            if (liveStream?.highlighted_entity) {
                set((state) => unhighlightEntityReducer(state, {
                    liveStreamId,
                    entityId: liveStream.highlighted_entity?.entity_id,
                    entityType: liveStream.highlighted_entity?.entity_type,
                }), false, 'applyLiveStreamStateSnapshot-unhighlightEntity');
            }
        }
        if (snapshot?.pinned_message?.message_id) {
            set((state) => setLiveStreamPinnedMessageReducer(state, {
                liveStreamId: liveStreamId,
                message: {
                    event_type: 'pin_message',
                    id: snapshot.pinned_message.message_id,
                    text: snapshot.pinned_message.text ?? '',
                    user_type: Enum_LiveStream_User_type.MODERATOR,
                    username: snapshot.pinned_message.username ?? '',
                },
            }), false, 'applyLiveStreamStateSnapshot-setLiveStreamPinnedMessage');
        }
        else {
            set((state) => unsetLiveStreamPinnedMessageReducer(state, { liveStreamId }), false, 'applyLiveStreamStateSnapshot-unsetLiveStreamPinnedMessage');
        }
    },
    updateLiveStreamEntity: (action) => {
        const { liveStreamId, payload } = action;
        set((state) => {
            switch (payload?.action_type) {
                case 'giveaway_collection':
                    return giveawayCollectionReducer(state, {
                        liveStreamId,
                        openGiveaway: payload.entity_id ?? null,
                    });
                case 'highlight_entity': {
                    let entities;
                    if (payload.entities?.length) {
                        entities = payload.entities.map(({ entity_id, entity_type }) => ({
                            entity_id,
                            entity_type,
                            winners: payload.winners,
                            tally: payload.tally,
                        }));
                    }
                    else {
                        entities = [
                            {
                                liveStreamId,
                                entity_id: payload.entity_id,
                                entity_type: payload.entity_type,
                                winners: payload.winners,
                                tally: payload.tally,
                            },
                        ];
                    }
                    return highlightEntitiesReducer(state, {
                        liveStreamId,
                        entities,
                    });
                }
                case 'unhighlight_entity': {
                    if (payload.entities?.length) {
                        const entities = payload.entities.map(({ entity_id, entity_type }) => ({
                            entity_id,
                            entity_type,
                            tally: payload.tally,
                        }));
                        const newEntities = unhighlightEntitiesReducer(state, {
                            liveStreamId,
                            entities,
                        });
                        return {
                            ...newEntities,
                            activeInteractionIds: state.activeInteractionIds.filter((id) => id !== entities[0].entity_id),
                            visibleInteractionIds: state.visibleInteractionIds.filter((id) => id !== entities[0].entity_id),
                        };
                    }
                    const newEntities = unhighlightEntityReducer(state, {
                        liveStreamId,
                        entityId: payload.entity_id,
                        entityType: payload.entity_type,
                        tally: payload.tally,
                    });
                    const poll = payload.entity_type === 'poll' ? payload : {};
                    const liveStream = state.liveStreams[liveStreamId];
                    const pollFromLiveStream = liveStream?.highlighted_entities?.find(({ entity_type, entity_id }) => entity_type === 'poll' || entity_id === poll?.entity_id) || liveStream?.highlighted_entity;
                    if (poll &&
                        (liveStream?.status === Enum_LiveStream_Status.ACTIVE ||
                            liveStream?.status === Enum_LiveStream_Status.PAUSED) &&
                        poll.entity_id === pollFromLiveStream?.entity_id) {
                        return { ...newEntities };
                    }
                    return {
                        ...newEntities,
                        activeInteractionIds: state.activeInteractionIds.filter((id) => id !== payload.entity_id),
                        visibleInteractionIds: state.visibleInteractionIds.filter((id) => id !== payload.entity_id),
                    };
                }
                case 'enable_entity': {
                    return enableEntityReducer(state, {
                        liveStreamId,
                        entityId: payload.entity_id,
                    });
                }
                case 'disable_entity': {
                    return disableEntityReducer(state, {
                        liveStreamId,
                        entityId: payload.entity_id,
                        entityType: payload.entity_type,
                    });
                }
                default: {
                    return state;
                }
            }
        }, false, 'updateLiveStreamEntity');
    },
    /**
     * Appends a list of chat messages and sorts them by elapsed time. Used by
     * chat hydrations (replay) and WS message_batch.
     * @param action
     * @returns
     */
    appendAndSortLiveStreamMessages: (action) => set((state) => {
        // check if livestream chat is disabled
        if (!state.params?.livestream_chat) {
            return state;
        }
        const { liveStreamId, messages } = action;
        return {
            liveStreamMessages: {
                ...state.liveStreamMessages,
                [liveStreamId]: uniqBy([
                    ...(state.liveStreamMessages[liveStreamId] || []),
                    ...(messages || []),
                ], 'id').sort(byElapsedTimeDesc),
            },
        };
    }, false, 'appendAndSortLiveStreamMessages'),
    /**
     * Appends a single message to the chat. Used by WS message and update_message events.
     * @param action
     * @returns
     */
    addLiveStreamMessage: (action) => set((state) => {
        if (!state.params?.livestream_chat) {
            return state;
        }
        const { liveStreamId, message } = action;
        const messages = liveStreamMessagesSelector(state, liveStreamId) || [];
        const index = messages.findIndex((m) => m.id === message.id);
        if (index === -1) {
            // Append new message to the bottom of the chat.
            // Sorting by elapsed time confused the users, specially when chat
            // moderation is enabled. Messages were being added and sorted but
            // users weren't scrolling up to verify that messages were truly
            // added. Revised UX is to add to the bottom of that chat as
            // messages arrive. Sorting by elapsed time is still used when
            // chat first loads.
            messages.push(message);
        }
        else {
            messages[index] = message;
        }
        return {
            liveStreamMessages: {
                ...state.liveStreamMessages,
                [liveStreamId]: messages,
            },
        };
    }, false, 'addLiveStreamMessage'),
    deleteLiveStreamMessage: (action) => {
        set((state) => {
            if (!state.params?.livestream_chat) {
                return state;
            }
            const { liveStreamId, messageId } = action;
            const messages = liveStreamMessagesSelector(state, liveStreamId) || [];
            const index = messages.findIndex((m) => m.id === messageId);
            if (index !== -1) {
                messages.splice(index, 1);
            }
            return {
                liveStreamMessages: {
                    ...state.liveStreamMessages,
                    [liveStreamId]: messages,
                },
            };
        }, false, 'deleteLiveStreamMessage');
    },
    replaceLiveStreamEvents: (action) => set((state) => {
        const { liveStreamId, events } = action;
        return {
            liveStreamEvents: {
                ...state.liveStreamEvents,
                [liveStreamId]: events,
            },
        };
    }, false, 'replaceLiveStreamEvents'),
    removePreviousLiveStreamEvent: (action) => set((state) => {
        const { liveStreamId } = action;
        return {
            liveStreamEvents: {
                ...state.liveStreamEvents,
                [liveStreamId]: state.liveStreamEvents[liveStreamId].slice(1),
            },
        };
    }, false, 'removePreviousLiveStreamEvent'),
    setLiveStreamPinnedMessage: (payload) => set((state) => setLiveStreamPinnedMessageReducer(state, payload), false, 'setLiveStreamPinnedMessage'),
    unsetLiveStreamPinnedMessage: (payload) => set((state) => unsetLiveStreamPinnedMessageReducer(state, payload), false, 'unsetLiveStreamPinnedMessage'),
    setLiveStreamTemporaryChatHighlightMessage: (payload) => set((state) => setLiveStreamTemporaryChatHighlightMessageReducer(state, payload), false, 'setLiveStreamTemporaryChatHighlightMessage'),
    unsetLiveStreamTemporaryChatHighlightMessage: (payload) => set((state) => unsetLiveStreamTemporaryChatHighlightMessageReducer(state, payload), false, 'unsetLiveStreamTemporaryChatHighlightMessage'),
    setLiveStreamState: (liveStreamState) => set({
        liveStreamState,
    }, false, 'setLiveStreamState'),
    setLiveStreamError: (liveStreamError) => set({
        liveStreamError,
    }, false, 'setLiveStreamError'),
    setLiveStreamWSError: (liveStreamWSError) => set({
        liveStreamWSError,
    }, false, 'setLiveStreamWSError'),
    setLiveStreamActions: ({ liveStreamId, actions, }) => set((state) => ({
        liveStreamActions: {
            ...state.liveStreamActions,
            [liveStreamId]: actions,
        },
    }), false, 'setLiveStreamActions'),
    removeLiveStreamActions: ({ liveStreamId }) => set((state) => ({
        liveStreamActions: omit(state.liveStreamActions, [liveStreamId]),
    }), false, 'removeLiveStreamActions'),
    requestUsernameChange: ({ ref, username, }) => {
        set({
            usernameChangeRequest: {
                ref,
                status: null,
                username,
                message: '',
            },
        }, false, 'requestUsernameChange');
    },
    updateUsernameChangeRequest: ({ status, username, message, }) => {
        const currentRequest = get().usernameChangeRequest;
        set({
            usernameChangeRequest: {
                ref: '-1',
                status,
                username: username ? username : currentRequest.username,
                message: message ?? '',
            },
        }, false, 'updateUsernameChangeRequest');
    },
    setIMAActive: (isIMAActive) => {
        set({ isIMAActive }, false, 'setIMAActive');
    },
    setIsVideoMuted: (muted) => {
        set({ isVideoMuted: muted }, false, 'setIsVideoMuted');
    },
    streamStarted: (liveStreamId) => {
        set((prevState) => {
            const prevVideo = prevState.video;
            if (prevVideo?.live_stream_id !== liveStreamId) {
                logInfo('streamStarted: live stream id mismatch');
                return prevState;
            }
            const newVideo = {
                ...prevVideo,
                clientExtension: {
                    ...prevVideo?.clientExtension,
                    isStreaming: true,
                },
            };
            return {
                video: newVideo,
            };
        });
    },
    streamEnded: (liveStreamId) => {
        set((prevState) => {
            const prevVideo = prevState.video;
            if (prevVideo?.live_stream_id !== liveStreamId) {
                logInfo('streamStarted: live stream id mismatch');
                return prevState;
            }
            const newVideo = {
                ...prevVideo,
                clientExtension: {
                    ...prevVideo?.clientExtension,
                    isStreaming: false,
                },
            };
            return {
                video: newVideo,
            };
        });
    },
    hydrateProductsCompleted: (videoId) => {
        set((state) => ({
            hasHydratedProducts: {
                ...state.hasHydratedProducts,
                [videoId]: true,
            },
        }), false, 'hydrateProductsCompleted');
    },
    setLiveStreamRunningAnnouncement: ({ liveStreamId, announcement, }) => {
        set((state) => ({
            liveStreamRunningAnnouncements: {
                ...state.liveStreamRunningAnnouncements,
                [liveStreamId]: announcement,
            },
        }), false, 'setLiveStreamRunningAnnouncement');
    },
    setVisibility: (isVisible) => {
        set({ isVisible }, false, 'setVisibility');
    },
    setFlowLinkId: (flowLinkId) => {
        set({ flowLinkId }, false, 'setFlowLinkId');
    },
    setLiveStreamChatInputState: (liveStreamChatInputState) => {
        set({ liveStreamChatInputState }, false, 'setLiveStreamChatInputState');
    },
    toggleLiveStreamChatInputState: () => {
        set(({ liveStreamChatInputState }) => ({
            liveStreamChatInputState: liveStreamChatInputState === ILiveStreamChatInputState.PUBLIC
                ? ILiveStreamChatInputState.PRIVATE
                : ILiveStreamChatInputState.PUBLIC,
        }), false, 'toggleLiveStreamChatInputState');
    },
    showLiveStreamBotGreeting: ({ liveStreamId }) => {
        set(({ liveStreamBotGreeting }) => ({
            liveStreamBotGreeting: {
                ...liveStreamBotGreeting,
                [liveStreamId]: true,
            },
        }), false, 'showLiveStreamBotGreeting');
    },
    showTriviaGiveawayAnswers: ({ liveStreamId, interactionId, }) => {
        if (!liveStreamId || !interactionId) {
            return;
        }
        set((state) => {
            const interaction = {
                ...liveStreamInteractionSelector(state, liveStreamId, interactionId),
            };
            const { liveStreams } = state;
            const liveStream = liveStreams[liveStreamId];
            if (!interaction || !liveStream) {
                return state;
            }
            interaction.show_answers = true;
            const toRet = {
                liveStreams: {
                    ...liveStreams,
                    [liveStreamId]: {
                        ...liveStream,
                        interactions: [
                            ...(liveStream.interactions ?? []).filter((i) => i.id !== interaction.id),
                            interaction,
                        ],
                    },
                },
            };
            return toRet;
        });
    },
    updateLiveStreamInteractions: ({ liveStreamId, payload, }) => {
        if (!liveStreamId) {
            return;
        }
        set((state) => {
            const { liveStreams } = state;
            const liveStream = liveStreams[liveStreamId];
            if (!liveStream) {
                return state;
            }
            const interaction = {
                ...payload.interaction,
            };
            // initialize trivia giveaway with show_answers
            // field does not exist in the payload from BE
            // so we need to set it to false
            if (payload.interaction.interaction_type === 'trivia_giveaway') {
                interaction.show_answers = false;
            }
            const toRet = {
                liveStreams: {
                    ...liveStreams,
                    [liveStreamId]: {
                        ...liveStream,
                        interactions: [
                            ...(liveStream.interactions ?? []).filter((i) => i.id !== payload.interaction.id),
                            payload.interaction,
                        ],
                    },
                },
            };
            return toRet;
        }, false, 'updateLiveStreamInteractions');
    },
    setShowGiveawayAnimation(value) {
        set(() => ({ showGiveawayAnimation: value }), false, 'setShowGiveawayAnimation');
    },
    setLiveStreamMessageRateLimit({ liveStreamId, payload, }) {
        set(() => ({
            liveStreamMessageRateLimit: {
                [liveStreamId]: payload,
            },
        }), false, 'setLiveStreamMessageRateLimit');
    },
    clearLiveStreamMessageRateLimit({ liveStreamId }) {
        set((state) => {
            const { liveStreamMessageRateLimit } = state;
            const { [liveStreamId]: _lsId, ...rest } = liveStreamMessageRateLimit;
            return { liveStreamMessageRateLimit: rest };
        }, false, 'setLiveStreamMessageRateLimit');
    },
    setViewerCountryCode: (viewerCountryCode) => {
        set(() => ({ viewerCountryCode }), false, 'setViewerCountryCode');
    },
    setIsLoaded: (isLoaded) => {
        set((state) => ({ ...state, isLoaded }), false, 'setIsLoaded');
    },
    incrementHeartsCount: (liveStreamId) => {
        set((state) => {
            const liveStream = state.liveStreams[liveStreamId];
            if (!liveStream) {
                return state;
            }
            return {
                liveStreams: {
                    ...state.liveStreams,
                    [liveStreamId]: {
                        ...liveStream,
                        hearts_count: (liveStream.hearts_count ?? 0) + 1,
                    },
                },
            };
        }, false, 'incrementHeartsCount');
    },
});
